/**
 * Privacy Policy page for IdeaWeave.
 * Required for Google OAuth verification. Content reflects actual data practices.
 */

import React from "react";

import { LegalPageLayout } from "./LegalPageLayout";

import "./LegalPage.scss";

export const PrivacyPage: React.FC = () => {
  return (
    <LegalPageLayout title="Privacy Policy" backHref="/" dark={false}>
      <p className="legal-page__updated">Last updated: February 2025</p>

      <div className="legal-page__body">
        <p>
          IdeaWeave ("we", "us", "our") is an AI-powered diagram creation tool.
          We are committed to protecting your privacy. This policy explains what
          information we collect, how we use it, and your rights regarding your
          data.
        </p>

        <h2>1. Information We Collect</h2>

        <h3>Account Information</h3>
        <p>
          When you sign in with Google, we receive your name, email address, and
          profile picture from Google. We use this to create and maintain your
          IdeaWeave account.
        </p>

        <h3>Content You Create</h3>
        <p>
          We store the diagrams you create and the text prompts you use to
          generate them. This data is stored in our cloud (via Supabase) to
          enable cloud storage and cross-device access. We use Supabase
          specifically to help you save, sync, and manage your diagrams across
          devices.
        </p>

        <h3>AI Processing</h3>
        <p>
          When you use our AI features, the text prompts you enter are sent to
          our AI backend to generate diagram content. We do not use your prompts
          to train AI models or share them with third parties for purposes other
          than providing the service.
        </p>

        <h3>Optional Feedback</h3>
        <p>
          If you submit feedback (e.g., bug reports or feature requests), we may
          collect your message, user ID, and technical information such as
          browser type and page URL to help us address your request.
        </p>

        <h2>2. How We Use Your Information</h2>
        <ul>
          <li>To provide and maintain the IdeaWeave service</li>
          <li>To sync your diagrams across devices via cloud storage</li>
          <li>To process your AI generation requests</li>
          <li>To respond to your feedback and support requests</li>
          <li>To improve our product and fix issues</li>
        </ul>

        <h2>3. Third-Party Services</h2>
        <p>We use the following services to operate IdeaWeave:</p>
        <ul>
          <li>
            <strong>Supabase</strong> — For authentication (Google sign-in) and
            cloud storage of your diagrams. Supabase stores your data securely
            and does not use it for purposes other than providing the service.
          </li>
          <li>
            <strong>AI Backend</strong> — Your prompts are sent to our AI
            backend to generate diagrams. We use reputable AI providers (e.g.,
            OpenAI, Anthropic) to process requests.
          </li>
          <li>
            <strong>Vercel</strong> — Our application is hosted on Vercel. They
            may collect standard web metrics (e.g., IP address) for hosting
            purposes.
          </li>
        </ul>

        <h2>4. We Do Not Sell or Share Your Data</h2>
        <p>
          <strong>
            We do not sell, rent, or disclose your personal information to third
            parties for marketing or advertising purposes.
          </strong>{" "}
          We only share data with the service providers listed above as
          necessary to operate IdeaWeave.
        </p>

        <h2>5. Data Retention</h2>
        <p>
          We retain your account and diagram data for as long as your account is
          active. If you delete your account, we will delete or anonymize your
          data within a reasonable period, except where required by law.
        </p>

        <h2>6. Your Rights</h2>
        <p>
          You may request access to, correction of, or deletion of your personal
          information. Contact us using the email below. If you are in the
          European Economic Area or UK, you may have additional rights under
          applicable data protection laws.
        </p>

        <h2>7. Contact Us</h2>
        <p>
          For privacy-related questions or requests, contact us at:{" "}
          <a href="mailto:wqxsam@outlook.com">wqxsam@outlook.com</a>
        </p>
      </div>
    </LegalPageLayout>
  );
};
